import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useState } from "react";
import { useGetBenefitSelectionKeywordMatchesQuery } from "store/queries/benefitSelection";
import styled from "styled-components";

import { normaliseKeywords } from "./selectionRules";

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 1rem 1.125rem;
  background: var(--surface-50);
  border: 1px solid var(--surface-200);
  border-radius: 10px;
`;

/** A "doesn't contain" word — the same red chip the rule cards use for one. */
export const NeverChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-family: monospace;
  font-size: 0.8125rem;
  padding: 0.1875rem 0.5rem;
  border-radius: 6px;
  background: #fdecec;
  color: #7f1d1d;
`;

/**
 * How many of the account's recent checks one word would affect. Its own request, made when the chip
 * renders, so the card never waits on a count; "…" while it loads.
 */
function KeywordCount({
  accountId,
  keyword,
}: {
  accountId: string;
  keyword: string;
}) {
  const { data, isFetching, isError } =
    useGetBenefitSelectionKeywordMatchesQuery({ accountId, keyword });
  const testId = `selectionRules.ignore.${keyword}.count`;
  if (isFetching || (!data && !isError)) {
    return (
      <span className="text-xs text-color-secondary" data-testid={testId}>
        …
      </span>
    );
  }
  if (isError || !data) {
    return (
      <span className="text-xs text-color-secondary" data-testid={testId}>
        count unavailable
      </span>
    );
  }
  return (
    <span className="text-xs text-color-secondary" data-testid={testId}>
      {`appears on lines in ${data.data.matchedChecks.toLocaleString(
        "en-US"
      )} of this account's last ${data.data.sampledChecks.toLocaleString(
        "en-US"
      )} checks`}
    </span>
  );
}

interface IIgnoreKeywordsBoxProps {
  accountId: string;
  value: string[];
  disabled?: boolean;
  onChange: (keywords: string[]) => void;
}

/**
 * CLE-2802 — "Always ignore lines containing": one account-wide list, applied before every rule and
 * every benefit. A word is added on Enter or when the input loses focus, so typed text is never
 * left on screen looking saved.
 */
export function IgnoreKeywordsBox({
  accountId,
  value,
  disabled,
  onChange,
}: IIgnoreKeywordsBoxProps) {
  const [text, setText] = useState("");

  const commit = () => {
    if (!text.trim()) return;
    onChange(normaliseKeywords([...value, text]));
    setText("");
  };

  return (
    <Box data-testid="selectionRules.ignore">
      <div className="flex align-items-baseline gap-3 flex-wrap">
        <label htmlFor="selectionRules.ignore.add" className="font-semibold">
          Always ignore lines containing
        </label>
        <span className="text-xs text-color-secondary">
          Applied before every rule, to every benefit — including deductible,
          out-of-pocket and physician copay.
        </span>
      </div>
      <div className="flex flex-column gap-2">
        {value.map((keyword) => (
          <div
            key={keyword}
            className="flex align-items-center gap-2 flex-wrap"
            data-testid={`selectionRules.ignore.${keyword}`}
          >
            <NeverChip>
              {keyword}
              {!disabled && (
                <Button
                  type="button"
                  icon="pi pi-times"
                  className="p-button-text p-button-sm p-button-rounded p-0"
                  style={{ width: "1.25rem", height: "1.25rem" }}
                  aria-label={`Remove ${keyword}`}
                  data-testid={`selectionRules.ignore.${keyword}.remove`}
                  onClick={() =>
                    onChange(value.filter((word) => word !== keyword))
                  }
                />
              )}
            </NeverChip>
            <KeywordCount accountId={accountId} keyword={keyword} />
          </div>
        ))}
        <InputText
          id="selectionRules.ignore.add"
          data-testid="selectionRules.ignore.add"
          className="p-inputtext-sm"
          style={{ width: "14rem" }}
          placeholder="Add a word…"
          value={text}
          disabled={disabled}
          onChange={(event) => setText(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commit();
            }
          }}
        />
      </div>
    </Box>
  );
}
